import { ArrowUpRight, Eye, Glasses, Sparkles } from "lucide-react";
import { IMAGES, whatsappUrl } from "@/lib/clinic";

const ITEMS = [
  {
    id: "presbicia",
    icon: Glasses,
    title: "Presbicia",
    lead: "Recuperá la nitidez de cerca.",
    description:
      "Evaluamos tu visión de cerca y diseñamos un plan a medida: anteojos, lentes de contacto multifocales o soluciones quirúrgicas para una vida sin depender de las gafas.",
    accent: "turquoise",
  },
  {
    id: "cataratas",
    icon: Sparkles,
    title: "Cataratas",
    lead: "Cirugía segura, mínima y efectiva.",
    description:
      "Diagnóstico temprano y cirugía con facoemulsificación, la técnica más avanzada para devolver la visión clara con recuperación rápida y ambulatoria.",
    accent: "navy",
  },
  {
    id: "lentes-intraoculares",
    icon: Eye,
    title: "Lentes Intraoculares",
    lead: "Lentes premium, visión personalizada.",
    description:
      "Seleccionamos el lente intraocular que mejor se adapta a tu estilo de vida: monofocales, tóricos, multifocales y EDOF para corregir varias distancias a la vez.",
    accent: "turquoise",
  },
];

export default function Patologias() {
  return (
    <section
      id="patologias"
      data-testid="patologias-section"
      className="py-20 md:py-32 bg-cream"
    >
      <div className="section-container">
        <div className="max-w-3xl">
          <span className="text-[11px] tracking-[0.24em] uppercase text-turquoise-700 font-semibold">
            Principales patologías que atendemos
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-navy-900 leading-tight">
            Diagnóstico, tratamiento y seguimiento con <span className="italic text-turquoise-700 font-light">mirada humana</span>.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-navy-800/70 leading-relaxed">
            Cada paciente es único. Trabajamos con tecnología de alta precisión combinada con una atención cercana para acompañarte en cada etapa del cuidado de tu visión.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <article
                key={item.id}
                data-testid={`patologia-card-${item.id}`}
                className="group relative flex flex-col justify-between rounded-3xl border border-navy-900/5 bg-white p-8 md:p-10 shadow-[0_8px_30px_rgba(11,27,61,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(11,27,61,0.2)]"
              >
                <div>
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-7 ${
                      item.accent === "navy"
                        ? "bg-navy-900 text-turquoise-400"
                        : "bg-turquoise-100 text-turquoise-700"
                    }`}
                  >
                    <Icon className="w-7 h-7" strokeWidth={1.7} />
                  </div>

                  <h3 className="font-display text-2xl sm:text-3xl font-medium text-navy-900 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-turquoise-700 font-medium text-sm">{item.lead}</p>
                  <p className="mt-5 text-navy-800/75 leading-relaxed text-[15px]">
                    {item.description}
                  </p>
                </div>

                <a
                  href={whatsappUrl(`Hola, me interesa consultar por ${item.title}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`patologia-cta-${item.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-navy-900 group-hover:text-turquoise-700 transition-colors"
                >
                  Consultar por WhatsApp
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </article>
            );
          })}
        </div>

        {/* Tech / equipment strip */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 rounded-3xl overflow-hidden relative h-80 lg:h-96">
            <img
              src={IMAGES.equipmentBlue}
              alt="Equipamiento oftalmológico de alta tecnología"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-navy-900/10 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 text-cream">
              <span className="text-[11px] tracking-[0.22em] uppercase text-turquoise-300 font-semibold">
                Tecnología
              </span>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl font-medium">
                Equipamiento de última generación
              </h3>
              <p className="mt-2 text-cream/80 text-sm max-w-lg">
                Tomografía, biometría óptica y microscopía especular para diagnósticos precisos y tratamientos personalizados.
              </p>
            </div>
          </div>
          <div className="lg:col-span-2 rounded-3xl overflow-hidden relative h-80 lg:h-96">
            <img
              src={IMAGES.equipmentOrange}
              alt="Dispositivo diagnóstico ocular"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/75 via-navy-900/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 text-cream">
              <span className="text-[11px] tracking-[0.22em] uppercase text-turquoise-300 font-semibold">
                Precisión
              </span>
              <h3 className="mt-2 font-display text-xl sm:text-2xl font-medium">
                Mediciones ópticas avanzadas
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
